import React, { useState } from "react"
import { useMutation } from "urql"

import PackageSessionConfirmModal, {
  PackageSessionConfirmAction
} from "../../packages/PackageSessionConfirmModal"
import { Button } from "../../../shared/Buttons"
import Typography from "../../../shared/Typography"
import { useToast } from "../../../../contexts/ToastContext"
import { formatPackageSessionTimestamp } from "../../../../utils/utils"

const MARK_PACKAGE_SESSION_USED_MUTATION = `
  mutation MarkPackageSessionUsed($packageId: ID!) {
    markPackageSessionUsed(packageId: $packageId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          createdAt
          startsAt
        }
      }
    }
  }
`

const MARK_PACKAGE_FINISHED_MUTATION = `
  mutation MarkPackageFinished($packageId: ID!) {
    markPackageFinished(packageId: $packageId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          createdAt
          startsAt
        }
      }
    }
  }
`

const UNDO_MANUALLY_MARKED_PACKAGE_SESSION_MUTATION = `
  mutation UndoManuallyMarkedPackageSession($appointmentId: ID!) {
    undoManuallyMarkedPackageSession(appointmentId: $appointmentId) {
      result
      errors
      package {
        id
        sessionsUsed
        sessionsUpcoming
        sessionsRemaining
        numberOfSessions
        manuallyMarkedAppointments {
          id
          createdAt
          startsAt
        }
      }
    }
  }
`

export type ManuallyMarkedAppointment = {
  id: string
  createdAt?: string | null
  startsAt?: string | null
}

export type PackageSessionData = {
  id: string
  name?: string | null
  sessionsUsed: number
  sessionsUpcoming?: number | null
  sessionsRemaining?: number | null
  numberOfSessions?: number | null
  manuallyMarkedAppointments: ManuallyMarkedAppointment[]
}

type MutationPackage = {
  id: string
  sessionsUsed: number
  sessionsUpcoming?: number | null
  sessionsRemaining?: number | null
  numberOfSessions?: number | null
  manuallyMarkedAppointments: ManuallyMarkedAppointment[]
}

type PackageSessionActionsProps = {
  packageData: PackageSessionData
  onSuccess: (updatedPackage: MutationPackage) => void
}

const PackageSessionActions: React.FC<PackageSessionActionsProps> = ({ packageData, onSuccess }) => {
  const { showToast } = useToast()
  const [busy, setBusy] = useState(false)
  const [undoingAppointmentId, setUndoingAppointmentId] = useState<string | null>(null)
  const [confirmState, setConfirmState] = useState<{
    action: PackageSessionConfirmAction
    appointmentId?: string
  } | null>(null)

  const [, markSessionUsed] = useMutation(MARK_PACKAGE_SESSION_USED_MUTATION)
  const [, markPackageFinished] = useMutation(MARK_PACKAGE_FINISHED_MUTATION)
  const [, undoManuallyMarkedSession] = useMutation(UNDO_MANUALLY_MARKED_PACKAGE_SESSION_MUTATION)

  const sessionsRemaining = packageData.sessionsRemaining ?? 0

  const handleMutationResult = (
    payload: { result?: string; errors?: string[]; package?: MutationPackage } | undefined,
    error: { message?: string } | undefined,
    successMessage: string
  ) => {
    if (payload?.result === "success" && payload.package) {
      showToast({ content: successMessage, type: "success" })
      onSuccess(payload.package)
      return true
    }

    const message =
      payload?.errors?.join(", ") ||
      error?.message?.replace("[GraphQL]", "") ||
      "Something went wrong. Please try again."
    showToast({ content: message, type: "error" })
    return false
  }

  const handleMarkSessionUsed = () => {
    setBusy(true)
    return markSessionUsed({ packageId: packageData.id }).then((res) => {
      handleMutationResult(res.data?.markPackageSessionUsed, res.error, "Session marked as used.")
      setBusy(false)
    })
  }

  const handleMarkPackageFinished = () => {
    setBusy(true)
    return markPackageFinished({ packageId: packageData.id }).then((res) => {
      handleMutationResult(res.data?.markPackageFinished, res.error, "Package marked as finished.")
      setBusy(false)
    })
  }

  const handleUndoManuallyMarkedSession = (appointmentId: string) => {
    setUndoingAppointmentId(appointmentId)
    return undoManuallyMarkedSession({ appointmentId }).then((res) => {
      handleMutationResult(
        res.data?.undoManuallyMarkedPackageSession,
        res.error,
        "Manually marked session undone."
      )
      setUndoingAppointmentId(null)
    })
  }

  const handleConfirm = async () => {
    if (!confirmState) return
    if (confirmState.action === "markOne") {
      await handleMarkSessionUsed()
    } else if (confirmState.action === "markFinished") {
      await handleMarkPackageFinished()
    } else if (confirmState.action === "undo" && confirmState.appointmentId) {
      await handleUndoManuallyMarkedSession(confirmState.appointmentId)
    }
    setConfirmState(null)
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        <Button
          type="secondary"
          size="small"
          className=""
          floatingInMobile={false}
          style={{}}
          disabled={busy || sessionsRemaining <= 0}
          onClick={() => setConfirmState({ action: "markOne" })}>
          {busy && !undoingAppointmentId ? "Marking…" : "Mark session used"}
        </Button>
        <Button
          type="tertiary"
          size="small"
          className=""
          floatingInMobile={false}
          style={{}}
          disabled={busy || sessionsRemaining <= 0}
          onClick={() => setConfirmState({ action: "markFinished" })}>
          Mark package finished
        </Button>
      </div>

      {packageData.manuallyMarkedAppointments.length > 0 && (
        <div className="mt-2">
          <Typography as="p" variant="small" className="mb-1 font-bold">
            Manually marked
          </Typography>
          <div className="flex flex-col gap-1">
            {packageData.manuallyMarkedAppointments.map((appointment) => {
              const undoBusy = undoingAppointmentId === appointment.id
              const dateLabel = formatPackageSessionTimestamp(
                appointment.startsAt || appointment.createdAt
              )

              return (
                <div key={appointment.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-gray-dark">{dateLabel || "Session"}</span>
                  <Button
                    type="link"
                    size="small"
                    className=""
                    floatingInMobile={false}
                    style={{}}
                    disabled={undoBusy || busy}
                    onClick={() =>
                      setConfirmState({ action: "undo", appointmentId: appointment.id })
                    }>
                    {undoBusy ? "Undoing…" : "Undo"}
                  </Button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <PackageSessionConfirmModal
        visible={!!confirmState}
        action={confirmState?.action ?? null}
        packageName={packageData.name}
        onClose={() => setConfirmState(null)}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

export default PackageSessionActions
